import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  InformationCard,
  type EntityValue,
} from '@folio/components';
import {
  useHasPolicyProductConfig,
  usePolicyProductConfig,
} from '@folio/utils/usePolicyProductConfig';
import {
  keyValueFieldsToObject,
  toKeyValueFields,
  useBranchControllerForkBranch,
  usePolicyControllerEditPolicy,
  usePolicyControllerGetPolicy,
} from 'korint-sdk';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type PolicyInformationCardProps = {
  allowEdition?: boolean;
  variant?: 'broker' | 'policyholder';
};
export function PolicyInformationCardModule({
  allowEdition = true,
  variant = 'broker',
}: PolicyInformationCardProps) {
  const hasInformationCardConfig = useHasPolicyProductConfig('policyInformationCard');
  const hasPolicyholderConfig = useHasPolicyProductConfig('policyholderPolicyInformationCard');
  if (!hasInformationCardConfig && !hasPolicyholderConfig) {
    return null;
  }

  const configKey =
    variant === 'policyholder' && hasPolicyholderConfig
      ? 'policyholderPolicyInformationCard'
      : 'policyInformationCard';
  const policyInformationCard = usePolicyProductConfig(configKey);
  const productCustomPath = usePolicyProductConfig('productCustomPath');
  const { t } = useTranslation();

  const { policyId, branchId } = useParams();

  const { data: policy, refetch } = usePolicyControllerGetPolicy(
    policyId ?? '',
    branchId ? { branchId } : undefined,
    {
      query: {
        select: data => ({
          ...data,
          customFields: keyValueFieldsToObject(data.customFields ?? []),
        }),
      },
    },
  );
  const customerId = policy?.customers?.[0];

  const { mutateAsync: editPolicy } = usePolicyControllerEditPolicy();
  async function editEntity(key: string, value: EntityValue) {
    if (!policyId) {
      console.warn('Tried to edit policy information card but policyId is not defined');
      return;
    }
    await editPolicy({
      policyId,
      params: branchId ? { branchId } : undefined,
      data: {
        customFields: toKeyValueFields({ [key]: value }),
      },
    });
    refetch();
  }

  const { mutateAsync: forkBranch } = useBranchControllerForkBranch();
  const navigate = useNavigate();
  async function redirectToForm() {
    if (!policyId || !customerId) {
      console.warn('Tried to edit policy information card but policyId or customer is not defined');
      return;
    }
    if (!branchId) {
      navigate(`/mta${productCustomPath}/${customerId}/${policyId}/new`);
      return;
    }

    const branch = await forkBranch({
      policyId,
      branchId,
      data: {
        branchType: 'NEW_BUSINESS',
      },
    });
    navigate(`/discovery${productCustomPath}/${customerId}/${policyId}/${branch.id}`);
  }

  if (!policyInformationCard) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Le module PolicyInformationCardModule n'est pas configuré</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (policy === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{policyInformationCard.title}</CardTitle>
        </CardHeader>
        <CardContent>La police n'existe pas</CardContent>
      </Card>
    );
  }

  const title = t(`${policy.productId}.policyInformationCard.title`, {
    defaultValue: policyInformationCard.title ?? '',
  });

  return (
    <InformationCard
      {...policyInformationCard}
      title={title}
      productId={policy.productId}
      entityType="policy"
      entity={policy}
      updateEntity={editEntity}
      redirectToForm={redirectToForm}
      allowEdition={allowEdition}
    />
  );
}
